import { isEmpty, pick } from 'lodash';
import { genAttributes } from '@onward-delivery/core';

import { PRICED_RATE_FIELDS } from '@/components/ShipmentForm/utilities/pricingFields';

export const CLONE_ORDER = [
    'custom_fields',
    'po_number',
    'carrier_id',
    'del_elev',
    'del_stairs',
    'delivery_stair_quantity',
    'dropoff_address_is_custom',
    'dropoff_address',
    'dropoff_city',
    'dropoff_email',
    'dropoff_full_address',
    'dropoff_lat',
    'dropoff_lng',
    'dropoff_location_info',
    'dropoff_location_type',
    'dropoff_location',
    'dropoff_name',
    'dropoff_phone',
    'dropoff_state',
    'dropoff_street',
    'dropoff_unit',
    'dropoff_zip',
    'package_seq_id',
    'freight_type',
    'manufacturer',
    'oms',
    'pickup_address_is_custom',
    'pickup_address',
    'pickup_city',
    'pickup_elevator',
    'pickup_email',
    'pickup_full_address',
    'pickup_lat',
    'pickup_lng',
    'pickup_location_info',
    'pickup_location_type',
    'pickup_location',
    'pickup_name',
    'pickup_phone',
    'pickup_stair_quantity',
    'pickup_stairs',
    'pickup_state',
    'pickup_street_address',
    'pickup_unit',
    'pickup_zip',
    'reference_id',
    'shipper_id',
    'warehouse_carrier_id',
];

export const CLONE_MM_FIELDS = [
    'has_middle_mile_origin',
    'middle_mile_origin_name',
    'middle_mile_origin_phone',
    'middle_mile_origin_email',
    'middle_mile_origin_address',
    'middle_mile_origin_unit',
    'middle_mile_origin_city',
    'middle_mile_origin_state',
    'middle_mile_origin_zip',
    'middle_mile_origin_location',
    'middle_mile_origin_location_type',
    'priced_as_middle_mile',
];

export const CLONE_ITEM = [
    'assembly_type',
    'cubes_per_unit',
    'description',
    'height',
    // Carried so a cloned non-physical line stays intangible — omitting it lets the column
    // default (false) turn it into a physical item row on the clone.
    'intangible',
    'is_return',
    'item_type_details',
    'item_type',
    'length',
    'quantity',
    'service_time',
    'sku',
    'total_cubes',
    'total_weight',
    'weight',
    'width',
];

// Build a clone's sibling name from a source order's identifiers. Falls back to the other identifier
// when one is missing so a null/empty base never renders as "null-S1" / "undefined-A1".
export const buildCloneName = (source, suffixLetter, index) => {
    const suffix = `${suffixLetter}${index}`;
    const orderBase = source?.order_number || source?.po_number;
    const poBase = source?.po_number || source?.order_number;
    return {
        order_number: orderBase ? `${orderBase}-${suffix}` : suffix,
        po_number: poBase ? `${poBase}-${suffix}` : suffix,
    };
};

// clearPoNumber nulls the PO to avoid the (shipper_id, po_number) unique-index collision on returns.
export const buildReturnClone = (order, { items, clearPoNumber = false, updates = {} } = {}) => {
    if (!order || isEmpty(order)) {
        return {};
    }

    // Returns flip pickup/dropoff; skip if the source is already a return.
    const doAttrs = genAttributes(order, 'DROPOFF');
    const puAttrs = genAttributes(order, 'PICKUP');
    const swapped = {
        ...Object.fromEntries(Object.entries(doAttrs).map(([attr, doAttr]) => [puAttrs[attr], order[doAttr]])),
        ...Object.fromEntries(Object.entries(puAttrs).map(([attr, puAttr]) => [doAttrs[attr], order[puAttr]])),
    };

    return {
        ...pick(order, [...CLONE_ORDER, ...CLONE_MM_FIELDS]),
        haulaway_items: [],
        known_availability: false,
        source_form: 'MANUAL',
        ...updates,
        ...(clearPoNumber ? { po_number: null } : {}),
        ...(order.order_type !== 'return' ? swapped : {}),
        order_type: 'return',
        order_number: `${order.order_number}-R`,
        original_order_id: order.order_id,
        special_instructions: order?.order_shipper?.special_instructions_settings?.['Return'] || '',
        reference_id: order.po_number,
        // serial_number/line_id carried here rather than in CLONE_ITEM: itemExchanged maps CLONE_ITEM
        // twice onto one order, and two rows sharing a line_id would violate items_order_line_id_key.
        itemsByOrderId: (items || order.itemsByOrderId || []).map((item) => ({
            ...pick(item, CLONE_ITEM),
            serial_number: item.serial_number,
            line_id: item.line_id,
            barcode: item.barcode,
            is_return: true,
        })),
    };
};

// Shared skeleton for order clones (attempt-billing + service split): copies the whitelisted order +
// middle-mile fields, links the clone to `namingSource` (the root/parent it siblings off, defaulting
// to `order`), carries miles/dates, and applies the priced rate fields. Callers layer on their
// specifics (status/type, items + wh_events vs services, driver, instructions).
export const buildBaseClone = ({ order, namingSource = order, priced, suffixLetter, index }) => ({
    ...pick(order, [...CLONE_ORDER, ...CLONE_MM_FIELDS]),
    ...buildCloneName(namingSource, suffixLetter, index),
    original_order_id: namingSource.order_id,
    miles: order.miles,
    delivery_date: order.delivery_date,
    pickup_date: order.pickup_date,
    ...pick(priced || {}, PRICED_RATE_FIELDS),
});
